import { useState } from "react";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import ErrorMessage from "@/components/common/ErrorMessage";
import AuthInputField from "@/components/UI/AuthInputField";
import { svgIcons } from "@/components/UI/SvgIcons";
import { getDeviceId, showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { errorTypes } from "@/utility/validation";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL_USER_SERVICE } from "@/api/constant";

const { EmailIcon, EyeCloseIcon } = svgIcons;

interface Step1Prop {
    email: string;
    deviceId: string;
}

interface Step2Prop {
    newPassword: string;
}

const ForgetPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<Step1Prop & Step2Prop>({
        defaultValues: {
            email: "",
            deviceId: getDeviceId(),
            newPassword: "",
        },
    });


    const sendToken = async (data: Step1Prop) => {
        setLoading(true);
        try {
            const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.auth}/forgot-password`;
            const response = await callApi(PATH, { method: "POST", data });
            setToken(response.otp);
            setStep(2);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async (newPassword: string) => {
        if (!token) return;
        setLoading(true);
        try {
            const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.auth}/reset-password/${token}`;
            const res = await callApi(PATH, { method: "POST", data: { newPassword } });
            if (res.statusCode === 200) {
                showToastNotification('success', res.message)
                navigate(RoutesLink.Login)
            }
        } catch (err) {
            ThrowError(err);
        } finally {
            setLoading(false);
        }
    };


    const onSubmit = async (data: Step1Prop & Step2Prop) => {
        if (step === 1) {
            sendToken(data);
        } else if (step === 2) {
            resetPassword(data.newPassword);
        }
    };

    return (
        <div className="mx-auto min-h-screen relative overflow-hidden">
            <div className="relative grid lg:grid-cols-2 items-center">
                {/* Video background */}
                <div className="relative h-dvh blur-sm lg:blur-none">
                    <video
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                    >
                        <source src="./assets/videos/login-video.mp4" type="video/mp4" />
                    </video>
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <img src="./assets/images/white-logo.png" alt="Logo" />
                    </span>
                </div>

                {/* Form */}
                <div className="w-[calc(100%-40px)] lg:h-full lg:w-full lg:relative flex items-center justify-center absolute bg-white/95 lg:bg-transparent -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rounded-lg">
                    <img
                        className="h-full w-full left-0 top-0 absolute object-cover"
                        src="./assets/images/login-bg-gradient.png"
                        alt="Background"
                    />
                    <div className="p-8 xl:p-10 relative max-h-dvh overflow-auto">
                        <div className="w-full mx-auto max-w-[500px]" uk-scrollspy="target: > *; cls: uk-animation-scale-up; delay: 100 ;repeat: true">
                            <h2 className="text-2xl lg:text-4xl mb-4">Welcome to Nass Foods</h2>
                            <p className="text-sm sm:text-base mb-5 lg:mb-12">
                                Secure access to your dashboard. Manage users, track activities, and oversee operations with ease.
                            </p>
                            <h3 className="font-semibold mb-5 lg:mb-8 sm:text-center capitalize text-lg sm:text-3xl">
                                {step === 1 ? "Reset Your Password" : "Enter New Password"}
                            </h3>

                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Step 1 - Email */}
                                {step === 1 && (
                                    <div className="relative mb-4">
                                        <label htmlFor="email" className="form-label">Email</label>
                                        <div className="relative">
                                            <AuthInputField
                                                placeholder="e.g. example@gmail.com"
                                                {...register("email", {
                                                    ...errorTypes.required("Email"),
                                                    ...errorTypes.email,
                                                })}
                                            />
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">{EmailIcon}</span>
                                        </div>
                                        <ErrorMessage message={errors.email?.message} />
                                    </div>
                                )}

                                {/* Step 2 - New Password */}
                                {step === 2 && (
                                    <div className="relative mb-4">
                                        <label htmlFor="newPassword" className="form-label">New Password</label>
                                        <div className="relative">
                                            <AuthInputField
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Enter your new password"
                                                {...register("newPassword", {
                                                    required: "Password is required",
                                                    minLength: { value: 6, message: "Minimum 6 characters" },
                                                })}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
                                            >
                                                {EyeCloseIcon}
                                            </button>
                                        </div>
                                        <ErrorMessage message={errors.newPassword?.message} />
                                    </div>
                                )}

                                <div className="flex items-center justify-end mb-5 sm:mb-10">
                                    <Link
                                        to={RoutesLink.Login}
                                        className="text-primary-dark hover:underline transition duration-500 capitalize"
                                    >
                                        Want to Login?
                                    </Link>
                                </div>

                                <button
                                    disabled={loading || isSubmitting}
                                    type="submit"
                                    className="btn-primary w-full"
                                >
                                    {loading ? (
                                        <LoaderCircle strokeWidth={2.5} className="w-5 h-5 animate-spin text-white" />
                                    ) : step === 1 ? "Send Token" : "Reset Password"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgetPassword;
